// src/utils/dateUtils.ts
import { DateTime } from 'luxon';
import { DATE_FORMATS, LOCALES } from '@/utils/constants';

/**
 * Конвертирует ISO строку даты из UTC в локальное время
 * 
 * @param isoDateString - строка даты в формате ISO (из API)
 * @param locale - локаль для форматирования
 * @returns объект DateTime в локальном времени
 */
export const utcToLocal = (
  isoDateString: string | null, 
  locale: string = LOCALES.DEFAULT
): DateTime | null => {
  if (!isoDateString) return null;
  
  try {
    return DateTime.fromISO(isoDateString, { zone: 'utc' })
      .setLocale(locale)
      .toLocal();
  } catch (error) {
    console.error('Error converting UTC to local time:', error);
    return null;
  }
};

/**
 * Конвертирует локальную дату в UTC для API
 *
 * @param isoDateString - строка даты в формате ISO из поля ввода даты
 * @returns DateTime в UTC
 */
export const localToUtc = (isoDateString: string | null): DateTime | null => {
  if (!isoDateString) return null;
  
  try {
    return DateTime.fromISO(isoDateString).toUTC();
  } catch (error) {
    console.error('Error converting local to UTC time:', error);
    return null;
  }
};

/**
 * Форматирует UTC дату из API для отображения в UI
 * 
 * @param isoDateString - строка даты в формате ISO из API (UTC)
 * @param includeTime - включать ли время в форматированную строку
 * @param locale - локаль для форматирования
 * @returns форматированная строка даты в локальном времени
 */
export const formatDateTime = (
  isoDateString: string | null, 
  includeTime: boolean = false, 
  locale: string = LOCALES.DEFAULT
): string => {
  if (!isoDateString) return '—';
  
  try {
    const localDateTime = utcToLocal(isoDateString, locale);
    if (!localDateTime) return '—';
    
    const format = includeTime 
      ? DATE_FORMATS.LUXON_DISPLAY_WITH_TIME 
      : DATE_FORMATS.LUXON_DISPLAY;
    
    return localDateTime.toFormat(format);
  } catch (error) {
    console.error('Error formatting date:', error);
    return isoDateString || '—';
  }
};

/**
 * Форматирует только время из ISO строки даты
 * 
 * @param isoDateString - строка даты в формате ISO из API
 * @param locale - локаль для форматирования
 * @returns строка времени в формате ЧЧ:ММ
 */
export const formatTime = (
  isoDateString: string | null, 
  locale: string = LOCALES.DEFAULT
): string => {
  if (!isoDateString) return '';
  
  try {
    const localDateTime = utcToLocal(isoDateString, locale);
    if (!localDateTime) return '';
    
    return localDateTime.toFormat(DATE_FORMATS.TIME_ONLY);
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

/**
 * Преобразует дату из локального формата для поля ввода в UTC формат для API
 * 
 * @param localDate - строка даты в формате YYYY-MM-DD (из input[type="date"])
 * @returns строка даты в формате ISO для API или пустая строка
 */
export const getUtcDateIsoString = (localDate: string | null): string => {
  if (!localDate) return '';
  
  try {
    const utcDateTime = localToUtc(localDate);
    if (!utcDateTime) return '';
    
    return utcDateTime.toISODate() || '';
  } catch (error) {
    console.error('Error converting to UTC date:', error);
    return localDate || '';
  }
};

/**
 * Преобразует строку даты в формат для input[type="date"]
 * 
 * @param isoDateString - строка даты в формате ISO из API
 * @returns строка даты в формате YYYY-MM-DD
 */
export const formatDateForInput = (isoDateString: string | null): string => {
  if (!isoDateString) return '';
  
  try {
    const localDateTime = utcToLocal(isoDateString);
    if (!localDateTime) return '';
    
    return localDateTime.toFormat(DATE_FORMATS.API_DATE);
  } catch (error) {
    console.error('Error formatting date for input:', error);
    
    // Запасной вариант - просто возвращаем первую часть ISO строки
    try {
      if (typeof isoDateString === 'string') {
        const parts = isoDateString.split('T');
        if (parts.length > 0) {
          return parts[0];
        }
      }
    } catch {
      // Игнорируем ошибку
    }
    
    return '';
  }
};

/**
 * Проверяет, является ли дата сегодняшней (учитывает локальное время пользователя)
 * 
 * @param isoDateString - строка даты в формате ISO
 * @returns true, если дата сегодняшняя
 */
export const isToday = (isoDateString: string | null): boolean => {
  if (!isoDateString) return false;
  
  try {
    const localDateTime = utcToLocal(isoDateString);
    if (!localDateTime) return false;
    
    const today = DateTime.local().startOf('day');
    return localDateTime.hasSame(today, 'day');
  } catch (error) {
    return false;
  }
};

/**
 * Проверяет, является ли дата вчерашней (учитывает локальное время пользователя)
 * 
 * @param isoDateString - строка даты в формате ISO
 * @returns true, если дата вчерашняя
 */
export const isYesterday = (isoDateString: string | null): boolean => {
  if (!isoDateString) return false;
  
  try {
    const localDateTime = utcToLocal(isoDateString);
    if (!localDateTime) return false;
    
    const yesterday = DateTime.local().minus({ days: 1 }).startOf('day');
    return localDateTime.hasSame(yesterday, 'day');
  } catch (error) {
    return false;
  }
};

/**
 * Форматирование даты с умными относительными значениями (сегодня, вчера)
 * 
 * @param isoDateString - строка даты в формате ISO
 * @param includeTime - включать ли время в форматированную строку
 * @param locale - локаль для форматирования
 * @returns форматированная строка даты с относительными значениями
 */
export const formatSmartDate = (
  isoDateString: string | null, 
  includeTime: boolean = false, 
  locale: string = LOCALES.DEFAULT
): string => {
  if (!isoDateString) return '—';
  
  try {
    // Проверяем относительные даты
    if (isToday(isoDateString)) {
      return includeTime 
        ? `Сегодня, ${formatTime(isoDateString, locale)}` 
        : 'Сегодня';
    }
    
    if (isYesterday(isoDateString)) {
      return includeTime 
        ? `Вчера, ${formatTime(isoDateString, locale)}` 
        : 'Вчера';
    }
    
    // Для других дат используем обычное форматирование
    return formatDateTime(isoDateString, includeTime, locale);
  } catch (error) {
    console.error('Error in smart date formatting:', error);
    return formatDateTime(isoDateString, includeTime, locale);
  }
};